import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2Icon, ShieldCheckIcon, StarIcon, HomeIcon, SquareIcon, ShowerHeadIcon, BathIcon, LayersIcon, ChevronDownIcon } from 'lucide-react';
import { DaBellaQuoteForm } from './DaBellaQuoteForm';
interface DaBellaLandingPageProps {
  initialService?: string;
  onClose: () => void;
}
export function DaBellaLandingPage({
  initialService,
  onClose
}: DaBellaLandingPageProps) {
  const [showForm, setShowForm] = useState(false);
  const [selectedService, setSelectedService] = useState<string>(initialService || '');
  const [zipCode, setZipCode] = useState('');
  const [isValidZip, setIsValidZip] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (initialService) {
      setSelectedService(initialService);
    }
  }, [initialService]);
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const handleZipChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/\D/g, '').slice(0, 5);
    setZipCode(val);
    setIsValidZip(val.length === 5);
  };
  const handleContinue = () => {
    if (isValidZip && selectedService) {
      setShowForm(true);
    }
  };
  if (showForm) {
    return <DaBellaQuoteForm initialZip={zipCode} serviceSlug={selectedService} onClose={onClose} data-id="element-638" />;
  }
  const services = [{
    id: 'roofing',
    name: 'Roofing',
    icon: <HomeIcon className="w-6 h-6" data-id="element-639" />,
    desc: 'Roof replacement & repair'
  }, {
    id: 'windows',
    name: 'Windows',
    icon: <SquareIcon className="w-6 h-6" data-id="element-640" />,
    desc: 'Window replacement'
  }, {
    id: 'siding',
    name: 'Siding',
    icon: <LayersIcon className="w-6 h-6" data-id="element-641" />,
    desc: 'Exterior siding'
  }, {
    id: 'baths',
    name: 'Bathroom',
    icon: <ShowerHeadIcon className="w-6 h-6" data-id="element-642" />,
    desc: 'Bath remodeling'
  }, {
    id: 'walk-in-tubs',
    name: 'Walk-in Tubs',
    icon: <BathIcon className="w-6 h-6" data-id="element-643" />,
    desc: 'Accessible tubs'
  }];
  const serviceLabel = services.find(s => s.id === selectedService)?.name || '';
  const heroImages: Record<string, string> = {
    roofing: 'https://images.unsplash.com/photo-1632759145351-1d592919f522?w=1920&q=80',
    windows: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1920&q=80',
    siding: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1920&q=80',
    baths: 'https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?w=1920&q=80',
    'walk-in-tubs': 'https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?w=1920&q=80'
  };
  const heroImage = heroImages[selectedService] || 'https://images.unsplash.com/photo-1600585154526-990dced4db0d?w=1920&q=80';
  const heroHeadlines: Record<string, string> = {
    roofing: 'Professional Roofing You Can Trust',
    windows: 'Beautiful Windows, Expert Installation',
    siding: "Transform Your Home's Exterior",
    baths: 'Your Dream Bathroom Awaits',
    'walk-in-tubs': 'Safe, Accessible Walk-in Tubs'
  };
  const heroHeadline = initialService ? heroHeadlines[initialService] || 'Quality Home Improvement' : 'Quality Home Improvement by DaBella';
  return <div className="min-h-screen bg-[#FAFAFA] font-sans" data-id="element-644">
      {/* Header */}
      <header className="bg-white py-4 px-6 md:px-12 flex justify-between items-center shadow-sm relative z-20" data-id="element-645">
        <div className="flex flex-col" data-id="element-646">
          <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-[-4px]" data-id="element-647">
            Partner
          </span>
          <div className="flex items-baseline gap-2" data-id="element-648">
            <span className="text-2xl md:text-4xl font-black text-[#1B5E20] tracking-tight" data-id="element-649">
              DaBella
            </span>
          </div>
          <span className="text-[10px] md:text-sm font-semibold text-gray-500" data-id="element-650">
            Roofing &bull; Windows &bull; Siding &bull; Baths
          </span>
        </div>
        <div data-id="element-651" />
      </header>

      {/* Offer Banner */}
      <section className="bg-[#1B5E20] py-4 md:py-6 border-t border-green-900" data-id="element-652">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-653">
          <h2 className="text-xl md:text-2xl font-bold text-white tracking-tight" data-id="element-654">
            Transform Your Home with Premium Quality Products
          </h2>
          <p className="text-green-100 text-sm mt-1" data-id="element-655">
            Serving homeowners in Idaho and Utah with excellence.
          </p>
        </div>
      </section>

      {/* Hero Section */}
      <section className="relative pt-12 pb-24 md:pt-20 md:pb-32 overflow-hidden" data-id="element-656">
        <div className="absolute inset-0 z-0" data-id="element-657">
          <img src={heroImage} alt="Home improvement" className="w-full h-full object-cover" data-id="element-658" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-transparent" data-id="element-659" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-660">
          <div className="flex flex-col md:flex-row items-center justify-between gap-12" data-id="element-661">
            {/* Left Content */}
            <motion.div className="flex-1 text-white" initial={{
            opacity: 0,
            x: -30
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.6
          }} data-id="element-662">
              <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight mb-4 leading-tight drop-shadow-lg" data-id="element-663">
                {heroHeadline}
              </h1>
              <p className="text-lg md:text-xl font-medium text-gray-200 drop-shadow-md max-w-lg" data-id="element-664">
                Get a free estimate from one of Idaho &amp; Utah's top-rated
                home improvement companies.
              </p>
            </motion.div>

            {/* Right Form Card */}
            <motion.div className="w-full max-w-md" initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} data-id="element-665">
              <div className="bg-white rounded-2xl shadow-2xl p-8 border border-gray-100" data-id="element-666">
                <h2 className="text-xl font-bold text-[#1B5E20] text-center mb-6" data-id="element-667">
                  {initialService ? 'Enter Your Zip Code To Start' : 'Select a Service & Enter Your Zip'}
                </h2>

                {/* Service dropdown (only if no initialService) */}
                {!initialService && <div className="mb-6 relative" ref={dropdownRef} data-id="element-668">
                    <button onClick={() => setDropdownOpen(!dropdownOpen)} className={`w-full flex items-center justify-between px-5 py-4 rounded-xl border-2 transition-all duration-200 bg-green-50 ${selectedService ? 'border-[#1B5E20]' : 'border-green-200'} ${dropdownOpen ? 'border-[#1B5E20] shadow-md' : 'hover:border-green-300'}`} data-id="element-669">
                      <div className="flex items-center gap-3" data-id="element-670">
                        {selectedService ? <>
                            <span className="text-[#1B5E20]" data-id="element-671">
                              {services.find(s => s.id === selectedService)?.icon}
                            </span>
                            <span className="font-bold text-[#1B5E20] text-lg" data-id="element-672">
                              {services.find(s => s.id === selectedService)?.name}
                            </span>
                          </> : <span className="text-gray-400 font-semibold text-lg" data-id="element-673">
                            Choose a service...
                          </span>}
                      </div>
                      <ChevronDownIcon className={`w-5 h-5 text-[#1B5E20] transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`} data-id="element-674" />
                    </button>

                    <AnimatePresence data-id="element-675">
                      {dropdownOpen && <motion.div initial={{
                    opacity: 0,
                    y: -8,
                    scaleY: 0.95
                  }} animate={{
                    opacity: 1,
                    y: 0,
                    scaleY: 1
                  }} exit={{
                    opacity: 0,
                    y: -8,
                    scaleY: 0.95
                  }} transition={{
                    duration: 0.15,
                    ease: 'easeOut'
                  }} className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl border-2 border-green-200 shadow-xl z-30 overflow-hidden origin-top" data-id="element-676">
                          {services.map(svc => <button key={svc.id} onClick={() => {
                      setSelectedService(svc.id);
                      setDropdownOpen(false);
                    }} className={`w-full flex items-center gap-3 px-5 py-4 text-left transition-colors duration-150 ${selectedService === svc.id ? 'bg-green-50 text-[#1B5E20]' : 'bg-white text-gray-700 hover:bg-green-50 hover:text-[#1B5E20]'} border-b border-green-100 last:border-b-0`} data-id="element-677">
                              <span className={`${selectedService === svc.id ? 'text-[#1B5E20]' : 'text-gray-400'}`} data-id="element-678">
                                {svc.icon}
                              </span>
                              <span className="font-bold text-lg" data-id="element-679">
                                {svc.name}
                              </span>
                              {selectedService === svc.id && <CheckCircle2Icon className="w-5 h-5 text-[#1B5E20] ml-auto" data-id="element-680" />}
                            </button>)}
                        </motion.div>}
                    </AnimatePresence>
                  </div>}

                {/* Show selected service badge when pre-selected */}
                {initialService && <div className="flex items-center justify-center gap-2 mb-6 bg-green-50 border border-green-200 rounded-lg px-4 py-2.5" data-id="element-681">
                    {services.find(s => s.id === initialService)?.icon}
                    <span className="font-bold text-[#1B5E20]" data-id="element-682">
                      {serviceLabel}
                    </span>
                  </div>}

                {/* Zip Code Input */}
                <div className="relative mb-6" data-id="element-683">
                  <input type="text" value={zipCode} onChange={handleZipChange} placeholder="Enter Zip Code" className="w-full h-16 px-6 text-2xl font-bold text-center bg-white border-2 border-gray-300 rounded-xl text-gray-900 focus:outline-none focus:border-[#1B5E20] focus:ring-1 focus:ring-[#1B5E20] transition-colors" maxLength={5} data-id="element-684" />
                  {isValidZip && <div className="absolute right-4 top-1/2 -translate-y-1/2 text-green-500" data-id="element-685">
                      <CheckCircle2Icon className="w-8 h-8" data-id="element-686" />
                    </div>}
                </div>

                <button onClick={handleContinue} disabled={!isValidZip || !selectedService} className={`w-full h-16 rounded-xl font-bold text-xl text-white transition-all shadow-lg ${isValidZip && selectedService ? 'bg-[#FF6F00] hover:bg-orange-700 shadow-orange-500/30 hover:shadow-orange-500/40 cursor-pointer' : 'bg-gray-300 cursor-not-allowed shadow-none'}`} data-id="element-687">
                  Get Free Estimate
                </button>

                {/* Trust Badges */}
                <div className="mt-8 flex items-center justify-center gap-4" data-id="element-688">
                  <div className="flex items-center gap-2 bg-green-50 px-3 py-1.5 rounded-lg border border-green-200" data-id="element-689">
                    <ShieldCheckIcon className="w-5 h-5 text-green-600" data-id="element-690" />
                    <span className="text-xs font-bold text-green-800 uppercase tracking-tight" data-id="element-691">
                      Licensed & Insured
                    </span>
                  </div>
                  <div className="flex items-center gap-2 bg-blue-50 px-3 py-1.5 rounded-lg border border-blue-200" data-id="element-692">
                    <div className="font-black text-blue-700 text-sm" data-id="element-693">BBB</div>
                    <span className="text-xs font-bold text-blue-800 uppercase tracking-tight" data-id="element-694">
                      A+ Rating
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Green Accent Bar */}
      <div className="w-full h-3 bg-[#1B5E20]" data-id="element-695" />

      {/* What to Expect Section */}
      <section className="py-20 bg-white" data-id="element-696">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-697">
          <div className="text-center mb-16" data-id="element-698">
            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900" data-id="element-699">
              What to Expect
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 text-center" data-id="element-700">
            {[{
            title: 'Choose Your Service',
            desc: 'Select from roofing, windows, siding, bathroom remodeling, or walk-in tubs. Our experts specialize in each area to deliver the best results.',
            img: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=400&h=400&fit=crop'
          }, {
            title: 'Get Your Free Estimate',
            desc: 'A DaBella representative will visit your home, assess the project, review options and pricing, and provide a no-obligation quote on the spot.',
            img: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=400&h=400&fit=crop'
          }, {
            title: 'Professional Installation',
            desc: 'Our factory-trained installers handle everything from start to finish, backed by industry-leading warranties for your peace of mind.',
            img: 'https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=400&h=400&fit=crop'
          }].map((item, idx) => <motion.div key={idx} className="flex flex-col items-center" initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: idx * 0.2,
            duration: 0.5
          }} data-id="element-701">
                <div className="w-48 h-48 rounded-full overflow-hidden mb-6 shadow-lg border-4 border-white" data-id="element-702">
                  <img src={item.img} alt={item.title} className="w-full h-full object-cover" data-id="element-703" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4" data-id="element-704">
                  {item.title}
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-705">{item.desc}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-[#FAFAFA]" data-id="element-706">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-707">
          <div className="text-center mb-12" data-id="element-708">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900" data-id="element-709">
              What Our Customers Say
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-710">
            {[{
            name: 'Mike R.',
            location: 'Boise, ID',
            text: 'DaBella replaced our entire roof in just two days. The crew was professional, clean, and the new roof looks incredible. Highly recommend!'
          }, {
            name: 'Jennifer & Tom S.',
            location: 'Salt Lake City, UT',
            text: 'We had 14 windows replaced and the difference is night and day. Our energy bills dropped significantly and the house feels so much quieter.'
          }, {
            name: 'Carol W.',
            location: 'Meridian, ID',
            text: 'The walk-in tub installation was seamless. The team was respectful of our home and the tub has been life-changing for my mobility.'
          }].map((review, idx) => <motion.div key={idx} className="bg-white p-8 rounded-xl shadow-sm border border-gray-200" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: idx * 0.1
          }} data-id="element-711">
                <div className="flex text-yellow-400 mb-4" data-id="element-712">
                  {[...Array(5)].map((_, i) => <StarIcon key={i} className="w-5 h-5 fill-current" data-id="element-713" />)}
                </div>
                <p className="text-gray-600 mb-6 italic" data-id="element-714">
                  &ldquo;{review.text}&rdquo;
                </p>
                <div data-id="element-715">
                  <p className="font-bold text-gray-900" data-id="element-716">{review.name}</p>
                  <p className="text-sm text-gray-500" data-id="element-717">{review.location}</p>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1B5E20] py-16" data-id="element-718">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-719">
          <div className="flex flex-col md:flex-row justify-between items-start gap-12 mb-16" data-id="element-720">
            <div className="flex flex-col gap-8" data-id="element-721">
              <div className="flex flex-col" data-id="element-722">
                <span className="text-[10px] font-bold text-white/70 uppercase tracking-wider mb-[-4px]" data-id="element-723">
                  Partner
                </span>
                <span className="text-3xl font-black text-white tracking-tight" data-id="element-724">
                  DaBella
                </span>
                <span className="text-[10px] font-semibold text-white/90" data-id="element-725">
                  Roofing &bull; Windows &bull; Siding &bull; Baths
                </span>
              </div>
              <p className="text-xs text-white/80 font-medium" data-id="element-726">
                &copy; {new Date().getFullYear()} findservicequotes.io. All
                Rights Reserved.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 md:gap-16" data-id="element-727">
              <div className="flex flex-col gap-4" data-id="element-728">
                <a href="#" className="text-xs text-white hover:underline font-medium" data-id="element-729">
                  Home
                </a>
                <a href="#" className="text-xs text-white hover:underline font-medium" data-id="element-730">
                  Privacy Policy
                </a>
                <a href="#" className="text-xs text-white hover:underline font-medium" data-id="element-731">
                  Terms Of Use
                </a>
              </div>
              <div className="flex flex-col gap-4" data-id="element-732">
                <a href="#" className="text-xs text-white hover:underline font-medium" data-id="element-733">
                  Accessibility Policy
                </a>
                <a href="#" className="text-xs text-white hover:underline font-medium" data-id="element-734">
                  Do Not Sell My Personal Information
                </a>
              </div>
            </div>
          </div>

          <div className="border-t border-white/20 pt-8" data-id="element-735">
            <p className="text-[10px] text-white/60 leading-relaxed text-justify" data-id="element-736">
              DaBella services are currently available in Idaho and Utah. All
              estimates are free and no-obligation. Actual project costs may
              vary based on scope, materials, and local conditions. Licensed and
              insured. Contact us for full warranty details.
            </p>
          </div>
        </div>
      </footer>
    </div>;
}